import React from "react";
import {
	Box,
	Button,
	Drawer,
	DrawerBody, DrawerCloseButton,
	DrawerContent,
	DrawerOverlay,
	useDisclosure,
	useMediaQuery
} from "~/foundation/Chakra";
import ChatBot from "./ChatBot";
import { Icon } from "../Icon";
import { breakpoints } from "~/foundation/Theme/variables/breakpoints";
import { type AliaSettingsModel } from "~/foundation/Jss/generated-types";
import { useTranslation } from "~/foundation/Dictionary";
import type { SearchDictionary } from "~/feature/Search/dictionary.ts"; // eslint-disable-line

export type ChatBotProps = {
	isRtl?: boolean;
	initialQuestion?: string;
	largeContentPadding?: boolean;
	formatMessageQuestions?: boolean;
	settings: AliaSettingsModel;
};

function ChatBotDrawer({ label, isRtl, ...props }: ChatBotProps & { label?: string }) {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [isMdBreakpointOrAbove] = useMediaQuery(`(min-width: ${breakpoints.md})`);
	const [t] = useTranslation<SearchDictionary>();

	return (
		<>
			<Button
				onClick={onOpen}
				h="fit-content"
				fontSize="xs"
				bg={label ? "primary.brightGrey" : "transparent"}
				ps={label ? { base: 0, md: 4 } : {}}
				pe={ label ? 2 : 0}
				py={ label ? 2 : 0 }
				borderRadius="full"
				borderColor="primary.aramcoBlue"
				borderStyle="solid"
				borderWidth={label ? 0 : "2px"}
				overflow="hidden"
				transition="color 500ms ease"
				aria-label={t("search.askAlia")}
				_hover={{
					color: "white",
					borderColor: "primary.aramcoLinkBlue",
					"& .alia-icon:before": label ? {
						transform: `scale(${label?.length * 0.8})`,
					} : {}
				}}
				flexShrink={0}
			>
				{label &&
					<Box as="span" zIndex={1} position="relative">{isMdBreakpointOrAbove && label}</Box>
				}
				<Box
					borderRadius="full"
					ms={label ? 2 : 0}
					transition="color 200ms ease"
					w={8}
					h={8}
					position="relative"
					className="alia-icon"
					_before={label ? {
						content: '""',
						borderRadius: "full",
						display: "block",
						position: "absolute",
						bgColor: "primary.darkBlue",
						w: "100%",
						h: "100%",
						left: 0,
						right: 0,
						transition: "transform 500ms ease"
					} : {}}
				>
					<Icon
						color={label ? "white" : "primary.aramcoBlue"}
						variant="Alia"
						w="100%"
						position="relative"
						zIndex={1}
					/>
				</Box>
			</Button>
			<Drawer placement={"end"} onClose={onClose} isOpen={isOpen}>
				<DrawerOverlay bgColor="blackAlpha.700">
					<DrawerContent maxW="container.md" maxH="100%">
						<DrawerBody position="relative" h="100%">
							<Box
								position="absolute"
								top={{ base: 4, md: "50%" }}
								insetStart={{ base: "unset", md: 0 }}
								insetEnd={{ base: 4, md: "unset" }}
								bgColor="primary.darkBlue"
								roundedStart="full"
								p={[".5rem", null, null, "1rem"]}
								color="white"
								transform={{ base: "none", md: `translateY(-50%) translateX(${isRtl ? "100%" : "-100%"})` }}>
								<DrawerCloseButton
									position="relative"
									zIndex={2}
									border="2px solid"
									p={2}
									borderRadius="full"
									transition="transform 250ms ease"
									w={["2.1875rem", null, null, "2.8125rem"]}
									h={["2.1875rem", null, null, "2.8125rem"]}
									_hover={{
										transform: [null, null, null, "rotate(45deg)"]
									}}
									sx={{
										"svg": {
											w: [".75rem", null, null, "1rem"],
											h: [".75rem", null, null, "1rem"]
										},
										willChange: "transform",
										backfaceVisibility: "hidden",
									}}
								/>
							</Box>
							<ChatBot {...props} />
						</DrawerBody>
					</DrawerContent>
				</DrawerOverlay>
			</Drawer>
		</>

	)
}

export default ChatBotDrawer;